import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, ShoppingBag, MessageCircle } from "lucide-react";

const STATUS_FLOW = { baru: "dihubungi", dihubungi: "selesai", selesai: "baru" };
const STATUS_STYLE = {
  baru: "text-[#A67C1E] border-[#A67C1E]/40 bg-[#A67C1E]/10",
  dihubungi: "text-[#1B4332] border-[#1B4332]/30 bg-[#1B4332]/5",
  selesai: "text-[#211B10]/50 border-[#211B10]/15",
};

export const PreOrdersManager = ({ client, API }) => {
  const [orders, setOrders] = useState([]);

  const load = () => client.get(`${API}/admin/preorders`).then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cycleStatus = async (o) => {
    try {
      await client.put(`${API}/admin/preorders/${o.po_id}/status`, { status: STATUS_FLOW[o.status] || "baru" });
      load();
    } catch {
      toast.error("Gagal mengubah status");
    }
  };

  const remove = async (o) => {
    if (!window.confirm(`Hapus pesanan PO dari "${o.name}"?`)) return;
    try {
      await client.delete(`${API}/admin/preorders/${o.po_id}`);
      toast.success("Pesanan dihapus");
      load();
    } catch {
      toast.error("Gagal menghapus pesanan");
    }
  };

  const waChat = (o) => {
    const phone = o.phone.startsWith("0") ? `62${o.phone.slice(1)}` : o.phone;
    window.open(
      `https://wa.me/${phone}?text=${encodeURIComponent(`Halo ${o.name}! Terima kasih sudah pre-order ${o.product_name} ukuran ${o.size} di NALURA. `)}`,
      "_blank", "noopener,noreferrer"
    );
  };

  return (
    <div className="mt-16" data-testid="preorders-manager">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl tracking-tight flex items-center gap-3">
          <ShoppingBag className="w-5 h-5 text-[#A67C1E]" /> Pesanan Pre-Order
        </h2>
        <span data-testid="preorders-count" className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#211B10]/55">
          {orders.filter((o) => o.status === "baru").length} baru / {orders.length} total
        </span>
      </div>

      <div className="border border-[#211B10]/10 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#211B10]/10 hover:bg-transparent">
              <TableHead className="text-[#211B10]/60 font-mono text-[10px] uppercase tracking-[0.2em]">Pemesan</TableHead>
              <TableHead className="text-[#211B10]/60 font-mono text-[10px] uppercase tracking-[0.2em]">Produk / Ukuran</TableHead>
              <TableHead className="text-[#211B10]/60 font-mono text-[10px] uppercase tracking-[0.2em]">Catatan</TableHead>
              <TableHead className="text-[#211B10]/60 font-mono text-[10px] uppercase tracking-[0.2em]">Status</TableHead>
              <TableHead className="text-right text-[#211B10]/60 font-mono text-[10px] uppercase tracking-[0.2em]">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.po_id} data-testid={`preorder-row-${o.po_id}`} className="border-[#211B10]/10 hover:bg-[#211B10]/[0.04]">
                <TableCell>
                  <p className="text-sm font-medium">{o.name}</p>
                  <p className="font-mono text-[10px] text-[#211B10]/55">{o.phone}</p>
                </TableCell>
                <TableCell>
                  <p className="text-xs text-[#211B10]/70">{o.product_name}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#A67C1E]">{o.size}</p>
                </TableCell>
                <TableCell className="max-w-[200px]">
                  <p className="text-xs text-[#211B10]/60 truncate">{o.note || "—"}</p>
                </TableCell>
                <TableCell>
                  <button
                    data-testid={`preorder-status-${o.po_id}`}
                    onClick={() => cycleStatus(o)}
                    title="Klik untuk ubah status"
                    className={`font-mono text-[9px] uppercase tracking-[0.15em] px-2 py-1 border cursor-pointer ${STATUS_STYLE[o.status] || STATUS_STYLE.baru}`}
                  >
                    {o.status}
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-2">
                    <Button data-testid={`preorder-wa-${o.po_id}`} title="Chat via WhatsApp" variant="outline" size="sm" onClick={() => waChat(o)} className="border-[#211B10]/15 bg-transparent hover:bg-[#211B10]/5 hover:text-[#1B4332]">
                      <MessageCircle className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`preorder-delete-${o.po_id}`} variant="outline" size="sm" onClick={() => remove(o)} className="border-[#211B10]/15 bg-transparent hover:bg-red-100 hover:text-red-700">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && (
              <TableRow className="border-[#211B10]/10 hover:bg-transparent">
                <TableCell colSpan={5} className="text-center py-10 text-[#211B10]/60 text-sm" data-testid="preorders-empty">
                  Belum ada pesanan PO. Pesanan dari form pre-order akan muncul di sini.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
